import { useEffect, useState } from 'react'
import useGetEnrolledCards from '@/src/card-linking/hooks/useGetEnrolledCards'
import CardLinkSuccess from '@/src/card-linking/components/CardLinkSuccess'
import CardLinkEnrollNewCardForm from '@/src/card-linking/components/CardLinkEnrollNewCardForm'
import CardLinkEnrolledCards from '@/src/card-linking/components/CardLinkEnrolledCards'
import usePostActivateGift from '@/src/card-linking/hooks/usePostActivateGift'
import CardLinkLoader from '@/src/card-linking/components/CardLinkLoader'
import { TCardLinkFunctions } from '@/src/card-linking/types'
import RemoveOrAddCardStep from '@/src/card-linking/components/RemoveOrAddCardStep'
import { UseMutationResult } from 'react-query'

interface CardLinkingFormProps {
    cardLinkSessionId: string
    functions: TCardLinkFunctions
    deleteCard: UseMutationResult<any, any, any, any>
    cardToCardUuid: string
    hideGiftDetailsButton?: boolean
    pushStepOnActivate?: boolean
    initialStep?: 'INITIAL' | 'REMOVE_OR_ADD_CARDS'
}

interface ICurrentCard {
    redemptionAccountId: string
    brand: string
    lastFour: string
    expiration: string
    nameOnCard: string
}

const CARD_LINK_STEP_INITIAL = 'INITIAL'
const CARD_LINK_STEP_FORM = 'FORM'
const CARD_LINK_STEP_CARD_LIST = 'CARD_LIST'
const REMOVE_OR_ADD_CARDS = 'REMOVE_OR_ADD_CARDS'
const CARD_LINK_SUCCESS = 'CARD_LINK_SUCCESS'

export default function CardLinkForm({
    cardLinkSessionId,
    functions,
    hideGiftDetailsButton,
    initialStep = CARD_LINK_STEP_INITIAL,
    deleteCard,
    cardToCardUuid,
}: CardLinkingFormProps) {
    const {
        mutate: postActivateGift,
        isSuccess: isSuccessActivateGift,
        isError: isErrorActivateGift,
    } = usePostActivateGift()

    const { data: enrolledCardsResponse, isError: isErrorEnrolledCards } =
        useGetEnrolledCards(cardLinkSessionId)
    const hasEnrolledCards = Boolean(
        enrolledCardsResponse?.data && enrolledCardsResponse?.data.length > 0
    )

    // if deleting, show delete step
    // if not, show loader until content is ready
    const isDeleting = initialStep === REMOVE_OR_ADD_CARDS
    const [adjustTextForNonActivate, setAdjustTextForNonActivate] =
        useState<boolean>(false)
    const [showLoader, setShowLoader] = useState<boolean>(!isDeleting)

    // The selected card from the list of enrolled cards or the card that was just submitted
    const [currentCard, setCurrentCard] = useState<ICurrentCard | null>(null)

    // The current step of the process - helps with showing the initial screen vs card list vs form
    const [currentStep, setCurrentStep] = useState<
        | typeof CARD_LINK_STEP_INITIAL
        | typeof CARD_LINK_STEP_CARD_LIST
        | typeof CARD_LINK_STEP_FORM
        | typeof REMOVE_OR_ADD_CARDS
        | typeof CARD_LINK_SUCCESS
    >(initialStep)

    const handleCardEnrollSuccess = async (cardLinkSuccessData: any) => {
        setShowLoader(true)
        setCurrentCard({
            redemptionAccountId: cardLinkSuccessData.data.id,
            brand: cardLinkSuccessData.data.brand,
            lastFour: cardLinkSuccessData.data.card_last_four,
            expiration: cardLinkSuccessData.data.card_expiration,
            nameOnCard: cardLinkSuccessData.data.name,
        })

        if (isDeleting) {
            setCurrentStep(CARD_LINK_SUCCESS)
            setShowLoader(false)
            return
        }

        await handleActivateGift(cardLinkSuccessData.data.id, cardLinkSessionId)
        setShowLoader(false)
    }

    const handleSelectCard = (selectedCard: any) => {
        setCurrentCard({
            redemptionAccountId: selectedCard.id,
            brand: selectedCard.brand,
            lastFour: selectedCard.card_last_four,
            expiration: selectedCard.card_expiration,
            nameOnCard: selectedCard.name,
        })
    }

    const handleActivateGift = async (
        redemptionAccountId: string,
        cardLinkSessionId: string
    ) => {
        postActivateGift(
            {
                redemption_account_id: redemptionAccountId,
                card_link_session_id: cardLinkSessionId,
            },
            {
                onSuccess: () => {
                    setCurrentStep(CARD_LINK_SUCCESS)
                    if (functions?.setIsActivating) {
                        functions.setIsActivating(true)
                    }
                },
            }
        )
    }

    const handleShowPaymentMethodForm = () => {
        setCurrentStep(CARD_LINK_STEP_FORM)
        setShowLoader(false)
    }

    const handleShowEnrolledCards = () => {
        setCurrentStep(CARD_LINK_STEP_CARD_LIST)
        setShowLoader(false)
    }

    useEffect(() => {
        if (!enrolledCardsResponse || currentStep !== CARD_LINK_STEP_INITIAL) {
            return
        }

        if (hasEnrolledCards) {
            handleShowEnrolledCards()
        } else {
            handleShowPaymentMethodForm()
        }
    }, [hasEnrolledCards, enrolledCardsResponse, currentStep])

    useEffect(() => {
        if (isSuccessActivateGift) {
            window.parent.postMessage(
                JSON.stringify({
                    message: 'card_link_complete',
                    date: Date.now(),
                    last_four: currentCard?.lastFour,
                    card_type: currentCard?.brand,
                    redemption_account_id: currentCard?.redemptionAccountId,
                }),
                // Security/SonarCloud - Only allow the current parent window to receive messages
                window.parent.origin
            )
        }
    }, [isSuccessActivateGift, currentCard])

    // refetches gift in card link gift details view and wallet
    useEffect(() => {
        if (isSuccessActivateGift && functions?.refetchGift) {
            functions.refetchGift()
        }

        if (isSuccessActivateGift && functions?.setIsActivating) {
            functions.setIsActivating(true)
        }
    }, [isSuccessActivateGift, functions])

    if (showLoader || currentStep === CARD_LINK_STEP_INITIAL) {
        return <CardLinkLoader />
    }

    if (currentStep === CARD_LINK_SUCCESS && currentCard) {
        return (
            <CardLinkSuccess
                cardId={currentCard.redemptionAccountId}
                cardNetwork={currentCard.brand}
                cardLastFour={currentCard.lastFour}
                hideGiftDetailsButton={hideGiftDetailsButton}
                adjustTextForNonActivate={adjustTextForNonActivate}
            />
        )
    }

    if (currentStep === CARD_LINK_STEP_CARD_LIST) {
        return (
            <CardLinkEnrolledCards
                enrolledCardsData={enrolledCardsResponse?.data}
                handleActivateGift={async () => {
                    if (currentCard) {
                        await handleActivateGift(
                            currentCard.redemptionAccountId,
                            cardLinkSessionId
                        )
                    }
                }}
                handleShowPaymentMethodForm={handleShowPaymentMethodForm}
                handleSelectCard={handleSelectCard}
            />
        )
    }

    if (currentStep === CARD_LINK_STEP_FORM) {
        return (
            <CardLinkEnrollNewCardForm
                cardLinkSessionId={cardLinkSessionId}
                handleSuccess={handleCardEnrollSuccess}
                functions={functions}
                isError={isErrorActivateGift || isErrorEnrolledCards}
                adjustTextForNonActivate={adjustTextForNonActivate}
            />
        )
    }

    if (currentStep === REMOVE_OR_ADD_CARDS) {
        return (
            <RemoveOrAddCardStep
                handleShowPaymentMethodForm={handleShowPaymentMethodForm}
                enrolledCardsData={enrolledCardsResponse?.data}
                deleteCard={deleteCard}
                cardToCardUuid={cardToCardUuid}
                refetchGift={functions?.refetchGift}
                setAdjustTextForNonActivate={setAdjustTextForNonActivate}
            />
        )
    }

    return <CardLinkLoader />
}
